import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { LoginType, useExistentEmailByFfpQuery, useSendSecureCodeMutation } from '@websky/graphql';
import { useAuthenticate } from '@features/auth';
import SubmitButton from '../SubmitButton/SubmitButton';
import Resend from '../../../../Contacts/components/Resend/Resend';
import { useTheme } from '../../../../theme';
import { STAY_LOGGED_IN } from '../../../../utils';
import { clearPhoneNumber } from '../../../../PhoneInput/utils';
import { AuthDuplicates } from '../../../../types';
import { AUTH_DUPLICATES, sessionStorageSet } from '../../../../cache';
import { Steps } from '../../../types';
import { useToggleable } from '../../../../hooks';
import { CdnIcon } from '@shared/ui';
import CodeInput from '../CodeInput/CodeInput';
import { Tooltip } from '../../../../BaseComponents';
import classNames from 'classnames';
import { SafeHtml } from '@shared/ui';
import { useLoginFormError } from '../../../hooks';

interface Props {
	login: string;
	loginType: LoginType;
	onSuccess: () => void;
	onError: (error: string) => void;
	onLoginFromEmailOrFfp: (email: string, code: string) => void;
	stayLoggedIn?: boolean;
	setStep: React.Dispatch<React.SetStateAction<Steps>>;
}

interface FormValues {
	code: string;
}

const CodeForm: React.FC<Props> = ({
	login,
	loginType,
	stayLoggedIn,
	onSuccess,
	onError,
	onLoginFromEmailOrFfp,
	setStep
}) => {
	const { CodeForm: css, BaseComponent: baseCss } = useTheme('LoginPage');
	const { t } = useTranslation('LoginPage');

	const [incorrectCode, setIncorrectCode] = useState(false);
	const [codeSent, setCodeSent] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const [authenticate] = useAuthenticate();
	const [sendCode, { loading: codeSending }] = useSendSecureCodeMutation();

	const { refetch: userExistentEmailByFfp } = useExistentEmailByFfpQuery({
		skip: true
	});

	const { setError, setDefaultError } = useLoginFormError(onError);

	const isLoginTypeFfp = loginType === LoginType.FFP;

	const resend = useCallback(async () => {
		setCodeSent(false);

		try {
			const result = await sendCode({
				variables: {
					params: {
						login: loginType === LoginType.Phone ? clearPhoneNumber(login) : login,
						loginType: loginType
					}
				}
			});

			if (result) {
				setCodeSent(true);
			} else {
				setDefaultError();
			}
		} catch (e) {
			setError(e);
		}
	}, [login, loginType]);

	const validate = useCallback((values: FormValues): object => {
		const errors = {};

		if (!values.code) {
			errors['code'] = t('Required');
		}

		return errors;
	}, []);

	const onAuthorize = useCallback(
		async (values: FormValues) => {
			try {
				await authenticate({
					variables: {
						params: {
							login: loginType === LoginType.Phone ? clearPhoneNumber(login) : login,
							loginType: loginType,
							secureCode: values.code,
							oneSession: !stayLoggedIn
						}
					}
				});

				setIncorrectCode(false);
				onSuccess();
			} catch {
				setIncorrectCode(true);
			}
		},
		[login, loginType, stayLoggedIn, onSuccess, authenticate]
	);

	const onUserExistentEmailByFfp = useCallback(
		async (code: string) => {
			setIncorrectCode(false);

			try {
				const { data } = await userExistentEmailByFfp({ login, password: code });

				return data?.ExistentEmailByFfp?.email ?? '';
			} catch {
				setIncorrectCode(true);

				return null;
			}
		},
		[login, userExistentEmailByFfp]
	);

	const onSubmit = useCallback(
		async (values: FormValues) => {
			setIsLoading(true);
			setIncorrectCode(false);

			if (stayLoggedIn) {
				localStorage.setItem(STAY_LOGGED_IN, 'true');
			} else {
				localStorage.removeItem(STAY_LOGGED_IN);
			}

			if (isLoginTypeFfp) {
				const existentEmailByFfp = await onUserExistentEmailByFfp(values.code);

				if (existentEmailByFfp !== null) {
					const authDuplicates: AuthDuplicates = {
						login,
						isExistDuplicate: !!existentEmailByFfp,
						code: values.code
					};

					sessionStorageSet(AUTH_DUPLICATES, authDuplicates);

					if (existentEmailByFfp) {
						// change step to login type selector
						onLoginFromEmailOrFfp(existentEmailByFfp, values.code);

						return;
					}
				}
			}

			await onAuthorize(values);
			setIsLoading(false);
		},
		[login, isLoginTypeFfp, stayLoggedIn, onAuthorize, onUserExistentEmailByFfp]
	);

	const message = useMemo(() => {
		switch (loginType) {
			case LoginType.Email:
				return t('An e-mail with code has been sent to you.');
			case LoginType.Phone:
				return t('Code sent to your phone.');
			default:
				return null;
		}
	}, [loginType]);

	const onBack = () => {
		setStep(Steps.LogIn);
	};

	const { isOpen: showPassword, toggle } = useToggleable(false);
	const EyeOpened = <CdnIcon icon={'Info/Show'} />;
	const EyeClosed = <CdnIcon icon={'Info/Hide'} />;

	return (
		<div className={css.root}>
			<div className={css.title}>{t(`Enter code_${loginType}`)}</div>
			<SafeHtml tag="div" className={css.message} html={message} />

			<Form<FormValues>
				validate={validate}
				onSubmit={onSubmit}
				render={({ handleSubmit }) => (
					<form onSubmit={handleSubmit}>
						<div className={css.row}>
							<div className={css.input_wrapper}>
								<Field
									name={'code'}
									render={({ input, meta }) => {
										const error = incorrectCode
											? isLoginTypeFfp
												? t('Please, check the password')
												: t('Please, check the code')
											: meta.error;
										const notValid = !!error && meta.touched;

										return (
											<CodeInput
												endAdornment={
													loginType === 'FFP' && (
														<Tooltip
															title={showPassword ? t('Hide') : t('Show')}
															enterDelay={1000}
														>
															<div
																onClick={toggle}
																className={classNames(css.passwordAdornment, {
																	[css.active]: showPassword
																})}
															>
																{showPassword ? EyeOpened : EyeClosed}
															</div>
														</Tooltip>
													)
												}
												label={t(`Verification code_${loginType}`)}
												inputClassName={css.input}
												TextFieldProps={{
													...input,
													onChange: ev => {
														if (incorrectCode) {
															setIncorrectCode(false);
														}

														input.onChange(ev);
													},
													error: notValid,
													helperText: notValid ? error : null,
													InputProps: {
														classes: {
															input: baseCss.input
														}
													}
												}}
												dynamicInputMask={isLoginTypeFfp && !showPassword}
												autoComplete={loginType === LoginType.FFP ? 'current-password' : 'off'}
											/>
										);
									}}
								/>
							</div>
							<div className={css.button_wrapper}>
								<SubmitButton loading={isLoading}>{t(`Continue_${loginType}`)}</SubmitButton>
							</div>
						</div>
					</form>
				)}
			/>

			{(loginType === LoginType.Email || loginType === LoginType.Phone) && (
				<div className={css.codeInfo}>{t('Contacts:Code was sent to {{login}}', { login })}</div>
			)}

			{!isLoginTypeFfp && (
				<Resend
					className={css.resend}
					labelClassName={css.resend__label}
					sendCode={resend}
					codeSent={codeSent}
					codeSending={codeSending}
					onBack={onBack}
				/>
			)}
		</div>
	);
};

export default CodeForm;
